import { MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Eyebrow, Reveal } from "@/components/site/motion";
import { CTABand } from "@/components/site/Sections";
import { FOUNDER_GRANT_ACHIEVEMENT } from "@/lib/content";

const CREDS = ["Chartered Accountant", "MS in Finance (US)", "10+ years in corporate finance", "Ex-Barclays & PACCAR"];
const REGIONS = [
  { name: "United States", note: "West Coast founders and small businesses" },
  { name: "United Kingdom", note: "Supporting London-based founders through our UK entity" },
  { name: "India", note: "Team support and delivery" },
];
const VALUES = [
  { title: "Clarity", text: "Numbers explained in plain language, so you always know what they mean for your next decision." },
  { title: "Partnership", text: "We work alongside you as an extension of your team, not as a distant service provider." },
  { title: "Purpose", text: "We choose to serve founders whose businesses make a positive difference in their communities." },
];

const Story = () => (
  <section data-testid="about-story" className="bg-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <div className="lg:sticky lg:top-32">
          <img data-testid="about-ananya-portrait" src="/assets/ananya-portrait.webp" width="580" height="751" alt="Ananya Jauhari, founder and lead strategist" className="w-full h-auto rounded-t-[10rem] rounded-b-lg" loading="lazy" />
          <p data-testid="about-founder-name" className="mt-6 font-display text-3xl text-[#082F24]">Ananya Jauhari</p>
          <p className="text-sm text-[#52635B]">Founder & Lead Strategist</p>
          <ul data-testid="about-founder-credentials" className="mt-6 space-y-3 border-t border-[#0F5A47]/20 pt-6 text-[#52635B]">
            {CREDS.map((c) => <li key={c} className="flex items-center gap-3 text-sm"><span className="h-1.5 w-1.5 rounded-full bg-[#0F5A47] shrink-0" />{c}</li>)}
          </ul>
          <p data-testid="about-founder-grant-achievement" className="mt-6 border-l-2 border-[#D4AF37] pl-4 text-sm font-medium text-[#082F24] leading-relaxed">{FOUNDER_GRANT_ACHIEVEMENT}</p>
        </div>
      </Reveal>
      <div className="lg:col-span-7 lg:col-start-6 space-y-8 text-[#52635B] text-base sm:text-lg leading-relaxed">
        <Reveal><Eyebrow>Meet Ananya</Eyebrow><h2 data-testid="about-story-title" className="section-heading mt-5">Finance should help you<br /><em>move forward.</em></h2></Reveal>
        <Reveal><p data-testid="about-story-intro">I'm Ananya Jauhari, a Chartered Accountant with an MS in Finance. After a decade at companies including Barclays and PACCAR, I saw how much easier decisions become when the right financial support is in the room.</p></Reveal>
        <Reveal><p>But for many small-business owners, that support is out of reach. You might have an accountant, software and a stack of reports, yet still feel alone when deciding whether to hire, invest or take the next step.</p></Reveal>
        <Reveal><p>That is why I founded Emerald Global Advisory: to bring the discipline of corporate finance to women-led and purpose-driven businesses, in a way that feels practical, personal and relevant to everyday decisions.</p></Reveal>
        <Reveal><p>We start by listening. What is taking too much of your time? Which numbers are unclear? What are you hoping to build? Then we shape the support around that, whether it means getting your books in order, planning your cash flow or thinking through a bigger move.</p></Reveal>
        <Reveal><p className="font-display text-3xl text-[#082F24]">You know your business.<br /><em>Let’s make the numbers work alongside you.</em></p></Reveal>
      </div>
    </div>
  </section>
);

const Team = () => (
  <section data-testid="about-team" className="bg-[#F3EFEA] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="max-w-3xl">
        <Eyebrow>Meet Our Expert Team</Eyebrow>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1] text-[#082F24]">A global collective with a <em>local</em> lens.</h2>
        <p className="mt-6 text-[#52635B]">Our diverse, globally-connected team combines deep expertise with a passion for empowering women-led and purpose-driven businesses.</p>
      </Reveal>
      <div className="mt-14 grid gap-4 lg:grid-cols-3">
        <Reveal testId="team-founder-card" className="lg:row-span-3 rounded-3xl bg-white border border-[#0F5A47]/12 p-8 sm:p-10 flex flex-col">
          <span className="font-mono-ega text-xs uppercase tracking-[0.2em] text-[#0F5A47]">Founder & Lead Strategist</span>
          <h3 className="mt-6 font-display text-4xl text-[#082F24]">Ananya Jauhari</h3>
          <p className="mt-4 text-[#52635B] leading-relaxed">A Chartered Accountant with an MS in Finance (US) and 10+ years of corporate finance experience at Barclays and PACCAR. Ananya brings rigorous financial thinking and a genuine interest in the people building each business.</p>
        </Reveal>
        {REGIONS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.08} testId={`team-region-${i + 1}`} className="lg:col-span-2 group rounded-3xl bg-[#082F24] text-[#FAF8F5] p-7 flex items-center gap-5 transition-colors duration-500 hover:bg-[#0F5A47]">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37]"><MapPin className="h-5 w-5" /></span>
            <div>
              <p className="font-display text-2xl">{r.name}</p>
              <p className="text-sm text-[#E8F3EE]/80">{r.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-6"><p className="text-[#52635B]"><strong className="text-[#082F24]">Our Global Collective</strong> is strategically based across the US, UK and India, offering diverse international perspectives and local market insights.</p></Reveal>
    </div>
  </section>
);

const Values = () => (
  <section data-testid="about-values" className="bg-[#FAF8F5] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal><Eyebrow>What guides us</Eyebrow></Reveal>
      <div className="mt-10 divide-y divide-[#0F5A47]/20 border-y border-[#0F5A47]/20">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.06} className="grid gap-4 md:grid-cols-12 py-10 items-baseline">
            <span className="md:col-span-1 font-mono-ega text-sm text-[#0F5A47]">0{i + 1}</span>
            <h3 className="md:col-span-4 font-display text-4xl sm:text-5xl text-[#082F24] italic">{v.title}</h3>
            <p className="md:col-span-6 md:col-start-7 text-[#52635B] leading-relaxed">{v.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <>
      <PageHero testId="about-hero" eyebrow="About Emerald Global Advisory" lines={["Behind every business,", <em key="f" className="text-[#D4AF37]">there is a person.</em>]} intro="We work with founders who care deeply about what they are building. Our role is to bring clarity to the numbers, perspective to the decisions and a little less weight to the journey." />
      <Story />
      <Team />
      <Values />
      <CTABand title="Let's write your next chapter together." />
    </>
  );
}
